/**
 * Session oynatma dongusu (uygulamanin kalbi).
 *
 * Arayuz CANLI ECUP oynaticisiyla ayni (Serap'in ornek ekran goruntusu):
 *   - ust bar: sol yuvarlak-dikdortgen GERI (ok) · orta nokta gostergeleri
 *     (yalniz mevcut dolu) · sag mor SAYAC (saat + kalan saniye)
 *   - orta: soru (mekanik cizici)
 *   - alt bar: sol acik-lila gecen-sure pili · orta alan-adi pili (meteor) ·
 *     sag BUYUK mor ILERI (ok / son soruda tik)
 *
 * Sozlesme: docs/EcupRunner/02-session-runner-contract.md.
 *   - ilk `Pending` item'dan basla (resume), sirayla ilerle
 *   - cevap dolunca "ileri" acilir; sure dolunca { timedOut: true } gonder (§6.2)
 *   - cevap KUYRUGA yazilir, cocuk sunucuyu beklemeden ilerler; kuyruk arka planda
 *     sirayla gonderilir (answerQueue.ts, anaokulu oturum sayfasiyla ayni model)
 *   - bir soru geri gidilebilir; eski secim `item.answer`'dan dolar (§6.3)
 *   - son soru cevaplaninca finish -> tamamlandi ekrani
 *
 * SAF: gercek API'yi de mock'u da `api` prop'u ile alir (App + POC ayni dongu).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { resolveMechanic } from "../mechanics/registry";
import type { MechanicAnswer, SessionItem } from "../mechanics/types";
import type { RunnerSession } from "./runnerApi";
import { ApiError } from "../api/client";
import { readSessionCodeFromUrl, readStudentIdFromToken } from "./token";
import { AnswerQueue, loadQueued, type QueueState } from "./answerQueue";
import { collectAssetUrls, preloadAssets } from "./preload";
import { PANEL_HOME } from "../config";
import { PRIMARY_PDF_NO, SUBSKILL_TO_AREA } from "./primaryQuestionMap";
import "../poc/poc.css";
import "./SessionPlayer.css";

export interface PlayerApi {
  submitAnswer(
    itemId: number,
    body: { attemptId: string; answer: MechanicAnswer; durationMs: number },
  ): Promise<void>;
  finish(): Promise<void>;
}

interface Props {
  session: RunnerSession;
  api: PlayerApi;
  /** Tamamlanınca (opsiyonel) — POC'ta baştan başlatmak için. */
  onCompleted?: () => void;
}

/* ---- ikonlar (FontAwesome yok; inline SVG) ---- */
const ArrowLeft = () => (
  <svg viewBox="0 0 24 24" className="rp-ico" aria-hidden="true">
    {/* tam ok: govde + uc (fa-arrow-left gibi) */}
    <path d="M20 12H5M11 6l-6 6 6 6" fill="none" stroke="currentColor" strokeWidth="2.4"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ArrowRight = () => (
  <svg viewBox="0 0 24 24" className="rp-ico" aria-hidden="true">
    <path d="M4 12h15M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2.4"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const CheckIco = () => (
  <svg viewBox="0 0 24 24" className="rp-ico" aria-hidden="true">
    <path d="M5 13l4 4L19 7" fill="none" stroke="currentColor" strokeWidth="2.8"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ClockIco = () => (
  <svg viewBox="0 0 24 24" className="rp-ico-sm" aria-hidden="true">
    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M12 7v5l3 2" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const MeteorIco = () => (
  <svg viewBox="0 0 24 24" className="rp-ico-sm" aria-hidden="true">
    <circle cx="16.5" cy="7.5" r="3.5" fill="currentColor" />
    <path d="M13 11L4 20M10 9l-3 3M15 14l-3 3" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" fill="none" />
  </svg>
);

function newAttemptId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `a-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

function firstPendingIndex(items: SessionItem[]): number {
  const i = items.findIndex((x) => x.status !== "Answered");
  return i === -1 ? Math.max(0, items.length - 1) : i;
}

/**
 * Oturumun sinifi (1–4). Canlida sablon kodundan ("ecup_primary_{sinif}_…") okunur; POC'ta
 * sablon "poc" oldugu icin soru kodu onekinden ("{sinif}sinif_…"). Bulunamazsa null.
 */
export function gradeOf(templateCode: string | undefined, items: SessionItem[]): number | null {
  const t = /^ecup_primary_(\d)_/i.exec(templateCode ?? "");
  if (t) return Number(t[1]);
  for (const it of items) {
    const m = /^(\d)sinif/i.exec(it.exerciseCode ?? "");
    if (m) return Number(m[1]);
  }
  return null;
}

/**
 * Soru-bazli tasarim kancasi (poc.css): 1. sinif "q-s34", diger siniflar "q-g2-s3" (pdfNo'lar cakisir).
 * POC kodu pdfNo'yu tasir ("…_s34"); canli kodda pdfNo yoktur, sinif|alan|alan-ici sira ile eslenir.
 */
export function questionHook(item: SessionItem | undefined, grade: number | null): string {
  if (!item) return "";
  const code = item.exerciseCode ?? "";
  let pdfNo: number | undefined;
  const poc = /s(\d+)$/.exec(code);
  if (poc) {
    pdfNo = Number(poc[1]);
  } else if (grade) {
    const order = /-(\d+)$/.exec(code);
    const area = SUBSKILL_TO_AREA[item.subAreaName];
    if (order && area) pdfNo = PRIMARY_PDF_NO[`${grade}|${area}|${order[1]}`];
  }
  if (!pdfNo) return "";
  const g = grade && grade > 1 ? `g${grade}-` : "";
  return ` q-${g}s${pdfNo}`;
}

/**
 * Kalan saniye. Başlangıç zamanı başka bir soruya aitse (soru yeni değişti, effect henüz
 * çalışmadı) tam süre döner; yeni soru hiçbir koşulda görülmeden "süre doldu" sayılmaz.
 */
export function remainingSeconds(
  limitSec: number,
  start: { ix: number; at: number },
  ix: number,
  now: number,
): number {
  if (start.ix !== ix) return limitSec;
  const gecen = Math.floor(Math.max(0, now - start.at) / 1000);
  return Math.max(0, Math.min(limitSec, limitSec - gecen));
}

/** Sunucu soruyu gezinme kuralı yüzünden reddetti mi (409 invalid_navigation). */
export function isNavigationLocked(e: unknown): boolean {
  return (
    typeof e === "object" && e !== null && (e as { code?: unknown }).code === "invalid_navigation"
  );
}

/** Geri gidilebilir mi: hedef soru, ilk cevaplanmamış sorudan en fazla bir önce olabilir. */
export function canGoBack(ix: number, frontier: number): boolean {
  return ix > 0 && ix - 1 >= frontier - 1;
}

function mmss(totalSec: number): string {
  const s = Math.max(0, totalSec);
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

/** Bekleyen cevap bu kadar süredir gönderilemiyorsa ekranda "bağlantı bekleniyor" notu çıkar. */
export const BAGLANTI_UYARI_MS = 8000;
/** Son soruda bekleyen cevaplar için en uzun bekleme; dolarsa "Tekrar dene" kutusu çıkar. */
export const BITIS_BEKLEME_MS = 60000;
/** "Seans tamamlanıyor" kutusu en az bu kadar görünür (ECUP'taki ilerleme çubuğu). */
export const BITIS_MIN_MS = 1500;

export function SessionPlayer({ session, api, onCompleted }: Props) {
  // Cihazda bekleyen (henüz sunucuya ulaşmamış) cevaplar da cevaplanmış sayılır; çocuk kaldığı yerden sürer.
  const [items, setItems] = useState<SessionItem[]>(() => {
    if (session.status === "Completed") return session.items;
    const bekleyen = new Map(loadQueued(session.sessionCode).map((q) => [q.itemId, q]));
    return session.items.map((x) => {
      const q = bekleyen.get(x.itemId);
      return q ? { ...x, status: "Answered", answer: q.body.answer } : x;
    });
  });
  const [ix, setIx] = useState(() => firstPendingIndex(items));
  const [answer, setAnswer] = useState<MechanicAnswer | null>(null);
  // Tamamlanmamis cok maddeli cevap (sure dolunca "atlandi" yerine bu gonderilir).
  const partialRef = useRef<MechanicAnswer | null>(null);
  const onAnswerChange = useCallback((a: MechanicAnswer | null, partial?: MechanicAnswer | null) => {
    setAnswer(a);
    partialRef.current = partial ?? null;
  }, []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [kuyruk, setKuyruk] = useState<QueueState>({ pending: 0, oldestAt: null });
  const queueRef = useRef<AnswerQueue | null>(null);
  // Kuyruk oturum boyunca tek: api nesnesi yeniden oluşsa da (POC) kuyruk yeniden kurulmaz.
  const apiRef = useRef(api);
  apiRef.current = api;
  useEffect(() => {
    const q = new AnswerQueue(session.sessionCode, (itemId, body) => apiRef.current.submitAnswer(itemId, body), setKuyruk);
    queueRef.current = q;
    if (session.status === "Completed") q.clear();
    setKuyruk(q.state);
    q.kick();
    return () => q.dispose();
  }, [session.sessionCode, session.status]);
  const [done, setDone] = useState(session.status === "Completed");
  // Son soru gönderilince ECUP'taki gibi "Seans tamamlanıyor..." kutusu; ardından bitiş (çalışma programı) kutusu.
  const [bitiriliyor, setBitiriliyor] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const sessionStartRef = useRef(Date.now());
  // Kalan soruların resim/sesleri oturum başında indirilir; bitene (ya da süre sınırına) kadar soru gösterilmez,
  // süre işlemez (preload.ts). İndirilecek dosya yoksa hemen başlar.
  const [hazirlik, setHazirlik] = useState<{ bitti: number; toplam: number } | null>(() => {
    if (session.status === "Completed") return null;
    const n = collectAssetUrls(items.slice(firstPendingIndex(items))).length;
    return n > 0 ? { bitti: 0, toplam: n } : null;
  });
  const hazirlaniyor = hazirlik !== null;
  useEffect(() => {
    if (!hazirlaniyor) return;
    let iptal = false;
    // Süre sınırı dolunca indirme arkada sürer; geç gelen ilerleme hazırlık ekranını yeniden açmasın (12 Eki 2026:
    // indirme 20 sn'yi aşınca ekran "Sorular hazırlanıyor"da kalıyordu).
    let bitti = false;
    const urls = collectAssetUrls(items.slice(firstPendingIndex(items)));
    void preloadAssets(urls, (inen, toplam) => {
      if (!iptal && !bitti) setHazirlik({ bitti: inen, toplam });
    }).then(() => {
      bitti = true;
      if (iptal) return;
      setHazirlik(null);
      // Süreler hazırlık bitince başlar.
      sessionStartRef.current = Date.now();
      itemStartRef.current = { ix: itemStartRef.current.ix, at: Date.now() };
      setNow(Date.now());
    });
    return () => {
      iptal = true;
    };
    // Yalnız açılışta bir kez.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Başlangıç zamanı hangi soruya ait olduğuyla birlikte tutulur: soru değiştiği render'da
  // eski sorunun başlangıcıyla kalan süre hesaplanıp yeni soru görülmeden "süre doldu"
  // gönderilmesin.
  const itemStartRef = useRef<{ ix: number; at: number }>({ ix, at: Date.now() });
  // Süre dolumu her soru ziyaretinde bir kez kuyruğa yazılır.
  const timeoutSentRef = useRef<number | null>(null);
  const item = items[ix];
  const isLast = ix === items.length - 1;
  // Sunucu yalnız ilk cevaplanmamış soruyu ve ondan bir öncekini kabul eder (RunnerAnswerNavigation).
  const frontier = useMemo(() => {
    const i = items.findIndex((x) => x.status !== "Answered");
    return i === -1 ? items.length : i;
  }, [items]);

  useEffect(() => {
    if (!item) return;
    setAnswer(item.answer ?? null);
    partialRef.current = null;
    itemStartRef.current = { ix, at: Date.now() };
    timeoutSentRef.current = null;
    setNow(Date.now());
  }, [ix, item]);

  /** Son soru: bekleyen bütün cevaplar gidince oturumu bitirir; olmazsa "Tekrar dene" kutusu. */
  const bitir = useCallback(async () => {
    const q = queueRef.current;
    setError(null);
    setBitiriliyor(true);
    setBusy(true);
    const t0 = Date.now();
    try {
      if (q && !(await q.drain(BITIS_BEKLEME_MS))) {
        throw new Error("Bazı cevaplar sunucuya ulaşmadı. İnternet bağlantısını kontrol edip tekrar deneyin.");
      }
      try {
        await api.finish();
      } catch (e) {
        // İlk bitirme isteği sunucuya ulaşıp yanıtı kaybolduysa yeniden deneme "zaten tamamlandı" der: başarı say.
        if (!(e instanceof ApiError && e.isConflict && e.code === "session_completed")) throw e;
      }
      const bekle = BITIS_MIN_MS - (Date.now() - t0);
      if (bekle > 0) await new Promise((r) => setTimeout(r, bekle));
      q?.clear();
      setBitiriliyor(false);
      setDone(true);
      onCompleted?.();
    } catch (e) {
      setBitiriliyor(false);
      setError(e instanceof Error ? e.message : "Oturum tamamlanamadı. Tekrar deneyin.");
    } finally {
      setBusy(false);
    }
  }, [api, onCompleted]);

  const advance = useCallback(
    (timedOut: boolean) => {
      if (!item || busy || done) return;
      if (!timedOut && answer == null) return;
      // Süre dolduğunda çocuk bir cevap seçmişse o cevap gönderilir. Çok maddeli soruda
      // yarım kalan işaretler de gönderilir (boş maddeler yanlış sayılır); hiç işaret yoksa timedOut.
      const payload: MechanicAnswer =
        answer ?? (timedOut && partialRef.current ? partialRef.current : { timedOut: true });

      setError(null);
      // Cevap kuyruğa yazılır; sunucuya gönderim arka planda sürer, çocuk beklemeden ilerler.
      queueRef.current?.enqueue(item.itemId, ix, {
        attemptId: newAttemptId(),
        answer: payload,
        durationMs: Date.now() - itemStartRef.current.at,
      });
      setItems((prev) =>
        prev.map((x, i) => (i === ix ? { ...x, status: "Answered", answer: payload } : x)),
      );

      if (isLast) void bitir();
      else setIx((i) => i + 1);
    },
    [answer, bitir, busy, done, isLast, item, ix],
  );

  useEffect(() => {
    if (done) return;
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, [done]);

  const limitSec = item && item.timeLimitSec > 0 ? item.timeLimitSec : 90;
  const remaining = remainingSeconds(limitSec, itemStartRef.current, ix, now);
  const elapsed = Math.floor((now - sessionStartRef.current) / 1000);

  useEffect(() => {
    if (done || busy || hazirlaniyor || !item || remaining > 0) return;
    if (itemStartRef.current.ix !== ix || timeoutSentRef.current === ix) return;
    timeoutSentRef.current = ix;
    advance(true);
  }, [remaining, done, busy, hazirlaniyor, item, ix, advance]);

  const Mech = useMemo(() => (item ? resolveMechanic(item.mechanic) : null), [item]);

  // Soru-bazli tasarim kancasi (poc.css .q-s<no> kurallari player'da da uygulansin).
  // Sinif namespace: 1. sinif "q-s34", 2. sinif "q-g2-s3" (pdfNo'lar cakisir).
  const grade = useMemo(() => gradeOf(session.templateCode, session.items), [session]);
  const qHook = useMemo(() => questionHook(item, grade), [item, grade]);

  if (done) {
    const code = readSessionCodeFromUrl(window.location.pathname) ?? "";
    // POC'ta onCompleted "baştan başla"dır; üretimde X paneli açar.
    const goHome = () => {
      if (onCompleted) {
        onCompleted();
        return;
      }
      window.location.href = PANEL_HOME;
    };
    // Yönetim (12 Eki 2026): seans sonunda çalışma programı yerine öğrenci profiline (rapor orada) ya da
    // Genel Bakış'a dönülür. Öğrenci Id'si runner token'ından okunur; yoksa Genel Bakış açılır.
    const studentId = code ? readStudentIdFromToken(code) : null;
    const goProfile = () => {
      if (onCompleted) {
        onCompleted();
        return;
      }
      window.location.href = studentId ? `${PANEL_HOME}/StudentReport/${studentId}` : PANEL_HOME;
    };
    return (
      <div className="rp-root">
        <div className="rp-done-scrim">
          <div
            className="rp-done-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rp-done-title"
          >
            <div className="rp-done-card-head">
              <h2 id="rp-done-title">Çalışma Tamamlandı</h2>
              <button
                className="rp-done-close"
                onClick={goHome}
                aria-label="Kapat"
              >
                ×
              </button>
            </div>
            <p className="rp-done-card-body">
              Çalışma başarıyla tamamlandı ve sonuçlar kaydedildi.
            </p>
            <div className="rp-done-card-actions">
              <button className="rp-done-cta rp-done-cta-ikincil" onClick={goHome}>
                Genel Bakış'a Dön
              </button>
              <button className="rp-done-cta" onClick={goProfile}>
                Öğrenci Profiline Dön
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (hazirlik) {
    const yuzde = hazirlik.toplam ? Math.round((hazirlik.bitti / hazirlik.toplam) * 100) : 0;
    return (
      <div className="rp-root">
        <div className="rp-finish-scrim" role="status" aria-live="polite">
          <div className="rp-finish-card">
            <p>Sorular hazırlanıyor...</p>
            <div className="rp-finish-bar rp-hazirlik-bar"><span style={{ width: `${yuzde}%` }} /></div>
          </div>
        </div>
      </div>
    );
  }

  if (!item) {
    return <div className="rp-root"><p className="rp-msg">Soru yok.</p></div>;
  }

  const answered = answer != null;
  const lowTime = remaining <= 10;

  return (
    <div className="rp-root">
      {/* ---- ust bar ---- */}
      <header className="rp-top">
        <button
          className="rp-btn rp-back"
          disabled={!canGoBack(ix, frontier) || busy}
          onClick={() => setIx((i) => (canGoBack(i, frontier) ? i - 1 : i))}
          aria-label="geri"
        >
          <ArrowLeft />
        </button>

        <div className="rp-dots">
          {items.map((it, i) => (
            <span key={it.itemId} className={`rp-dot${i === ix ? " current" : ""}${i < ix ? " done" : ""}`} />
          ))}
        </div>

        <div className={`rp-btn rp-timer${lowTime ? " low" : ""}`} aria-label="kalan süre">
          <ClockIco />
          <span className="rp-time">{remaining}</span>
        </div>
      </header>

      {/* ---- soru ---- */}
      <main className={`rp-main${grade ? ` poc-g${grade}` : ""}${qHook}`}>
        {Mech ? (
          <Mech key={item.itemId} item={item} onAnswerChange={onAnswerChange} />
        ) : (
          <p className="rp-msg">Bu soru tipi desteklenmiyor: {item.mechanic}</p>
        )}
      </main>

      {error && !bitiriliyor && (
        <div className="rp-error" role="alert">
          {error}
          {isLast && items[ix]?.status === "Answered" && (
            <button className="rp-error-retry" onClick={() => void bitir()}>Tekrar dene</button>
          )}
        </div>
      )}
      {!error && kuyruk.pending > 0 && kuyruk.oldestAt !== null && now - kuyruk.oldestAt >= BAGLANTI_UYARI_MS && (
        <div className="rp-sync" role="status" aria-live="polite">
          İnternet bağlantısı bekleniyor, cevaplar kaydedilecek…
        </div>
      )}

      {bitiriliyor && (
        <div className="rp-finish-scrim" role="status" aria-live="polite">
          <div className="rp-finish-card">
            <p>Seans tamamlanıyor...</p>
            <div className="rp-finish-bar"><span /></div>
          </div>
        </div>
      )}

      {/* ---- alt bar ---- */}
      <footer className="rp-bottom">
        <div className="rp-frame rp-elapsed">{mmss(elapsed)}</div>
        <div className="rp-frame rp-area">
          <span className="rp-area-icon"><MeteorIco /></span>
          <span className="rp-area-name">{item.subAreaName}</span>
        </div>
        <button
          className="rp-btn rp-forward"
          disabled={(!answered && remaining > 0) || busy}
          onClick={() => advance(!answered)}
          aria-label={isLast ? "bitir" : "ileri"}
        >
          {isLast ? <CheckIco /> : <ArrowRight />}
        </button>
      </footer>
    </div>
  );
}
